// Authenticated shell (AppShell): 240px sidebar ≥1024px; below that a top bar
// with a disclosure menu revealing the same nav. Structure follows shadcn-ui/ui
// apps/v4/registry/new-york-v4/blocks/sidebar-07 (nav-main) — MIT — without the
// Sheet/cookie machinery. The Admin nav group renders only for role=admin
// (cosmetic — the server enforces authz).

import { useEffect, useId, useRef, useState, type MouseEvent } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, Glyph, Logo, type GlyphName } from "@/ui";
import { ThemeControl } from "@/components/ThemeControl";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { SUPPORT_EMAIL, supportMailto } from "@/lib/support";

interface NavItem {
  to: string;
  label: string;
  icon: GlyphName;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Akun", icon: "person", end: true },
  { to: "/license", label: "Lisensi", icon: "key" },
  { to: "/buy", label: "Beli", icon: "cart" },
  { to: "/purchases", label: "Pembelian", icon: "receipt" },
  { to: "/download", label: "Unduh", icon: "download" },
  { to: "/koneksi-ai", label: "Koneksi AI", icon: "chip" },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin", label: "Ringkasan", icon: "layout", end: true },
  { to: "/admin/users", label: "Pengguna", icon: "users" },
  { to: "/admin/orders", label: "Pesanan", icon: "package" },
  { to: "/admin/licenses", label: "Lisensi", icon: "key" },
  { to: "/admin/pricing", label: "Harga", icon: "tag" },
  { to: "/admin/coupons", label: "Kupon", icon: "ticket" },
  { to: "/admin/commissions", label: "Komisi", icon: "coins" },
  { to: "/admin/webhooks", label: "Webhook", icon: "link" },
  { to: "/admin/audit", label: "Audit", icon: "logs" },
];

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    "relative flex h-11 items-center gap-3 rounded-md px-3 text-sm font-medium text-fg-1 no-underline",
    "transition-colors duration-150 hover:bg-surface-1 hover:text-fg-0 lg:h-10",
    isActive &&
      "bg-surface-2 text-fg-0 hover:bg-surface-2 before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-accent-ink",
  );
}

function SidebarNav({ isAdmin }: { isAdmin: boolean }) {
  return (
    <nav aria-label="Navigasi utama" className="flex flex-col gap-0.5">
      {NAV.map((item) => (
        <NavItemLink key={item.to} item={item} />
      ))}
      {isAdmin && (
        <>
          <span className="block px-3 pt-5 pb-1 text-sm font-medium text-fg-2">Admin</span>
          {ADMIN_NAV.map((item) => (
            <NavItemLink key={item.to} item={item} />
          ))}
        </>
      )}
    </nav>
  );
}

function NavItemLink({ item }: { item: NavItem }) {
  return (
    <NavLink to={item.to} end={item.end} className={navLinkClass}>
      <Glyph name={item.icon} className="size-4 shrink-0" />
      {item.label}
    </NavLink>
  );
}

// Rendered in both the desktop sidebar and the mobile disclosure menu, so the
// theme toggle ships with the account controls: Keluar and the toggle share one
// row, then support. The toggle is a 44px icon-only square (ui/DESIGN.md §10),
// which would read as an orphan on a row of its own, so it sits at the end of
// the Keluar row: ghost beside outlined, neither shouting.
function SidebarFooter({ onLogout }: { onLogout: () => Promise<void> }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Button variant="quiet" className="flex-1" onClick={onLogout}>
          <Glyph name="logOut" />
          Keluar
        </Button>
        <ThemeControl />
      </div>
      <a
        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-fg-2 no-underline transition-colors duration-150 hover:text-fg-0"
        href={supportMailto("Bantuan")}
      >
        <Glyph name="help" className="size-4 shrink-0" />
        <span>
          Bantuan — {SUPPORT_EMAIL}
        </span>
      </a>
    </div>
  );
}

function Brand() {
  return (
    <span className="flex items-center gap-2">
      <Logo />
      <span className="text-sm text-fg-2">Akun</span>
    </span>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const menuId = useId();

  // Admin data tables need more width than the 760px account column.
  const wide = location.pathname.startsWith("/admin");
  const isAdmin = user?.role === "admin";

  // Any navigation — including a tap on the current page's own link, which
  // still produces a new location key — closes the mobile menu.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      menuButtonRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  async function handleLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  // Move focus into <main> without a hash navigation: "#konten" in the URL
  // would add a history entry and hand the router a new location.
  function skipToContent(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    mainRef.current?.focus();
  }

  return (
    <div data-slot="app-shell" className="min-h-dvh bg-surface-0 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)]">
      {/* Skip link (DESIGN.md §10) — same copy and treatment as ahcut.com's
          Base layout. Fixed, so it is out of the grid flow and visible on focus
          at any scroll position. */}
      <Button
        asChild
        variant="secondary"
        className="fixed top-3 left-4 z-50 -translate-y-24 focus:translate-y-0"
      >
        <a href="#konten" onClick={skipToContent}>
          Lewati ke konten
        </a>
      </Button>

      <aside
        data-slot="app-sidebar"
        className="sticky top-0 hidden h-dvh flex-col gap-6 overflow-y-auto border-r border-line px-3 py-5 lg:flex"
      >
        <div className="px-3">
          <Brand />
        </div>
        <div className="flex-1">
          <SidebarNav isAdmin={isAdmin} />
        </div>
        <SidebarFooter onLogout={handleLogout} />
      </aside>

      <header data-slot="app-topbar" className="sticky top-0 z-40 border-b border-line bg-surface-0 lg:hidden">
        <div className="flex h-14 items-center justify-between gap-3 px-4">
          <Brand />
          <Button
            ref={menuButtonRef}
            variant="ghost"
            size="icon"
            aria-label="Menu navigasi"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Glyph name={menuOpen ? "close" : "menu"} />
          </Button>
        </div>
        <div
          id={menuId}
          hidden={!menuOpen}
          className="max-h-[calc(100dvh-3.5rem)] overflow-y-auto border-t border-line px-3 pt-2 pb-4"
        >
          <SidebarNav isAdmin={isAdmin} />
          <div className="mt-4 border-t border-line pt-4">
            <SidebarFooter onLogout={handleLogout} />
          </div>
        </div>
      </header>

      {/* tabIndex=-1: focusable by the skip link only; no ring on a non-control. */}
      <main
        id="konten"
        ref={mainRef}
        tabIndex={-1}
        data-slot="app-main"
        className="min-w-0 px-4 py-8 focus:outline-none sm:px-6 lg:px-10 lg:py-10"
      >
        <div
          data-slot="app-main-inner"
          className={cn("mx-auto flex w-full flex-col gap-8", wide ? "max-w-app" : "max-w-app-narrow")}
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}
